import { getAccessToken } from "../auth/keycloak";

export const API_BASE_URL = normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL);

export class ApiHttpError extends Error {
  status: number;
  payload: unknown;

  constructor(status: number, payload: unknown) {
    super(resolveApiErrorMessage(status));
    this.name = "ApiHttpError";
    this.status = status;
    this.payload = payload;
  }
}

export async function apiFetch(input: string | URL, init?: RequestInit) {
  const headers = new Headers(init?.headers ?? {});
  const token = await getAccessToken();

  if (typeof init?.body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return await fetch(buildApiUrl(input), {
    ...init,
    headers,
  });
}

export async function apiJson<T>(input: string | URL, init?: RequestInit): Promise<T> {
  const response = await apiFetch(input, init);
  const payload = (await response.json().catch(() => null)) as T;

  if (!response.ok) {
    throw new ApiHttpError(response.status, payload);
  }

  return payload;
}

export function buildApiUrl(input: string | URL, baseUrl = API_BASE_URL) {
  const value = input.toString();
  if (!baseUrl) return value;
  if (value.startsWith("/api")) return `${baseUrl}${value}`;

  const sameOriginApiPath = readSameOriginApiPath(value);
  if (!sameOriginApiPath) return value;
  return `${baseUrl}${sameOriginApiPath}`;
}

function readSameOriginApiPath(value: string) {
  if (typeof window === "undefined") return null;

  const url = new URL(value, window.location.origin);
  if (url.origin !== window.location.origin) return null;
  if (!url.pathname.startsWith("/api")) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

export function resolveApiErrorMessage(status: number) {
  if (status === 401) return "Session expiree ou utilisateur non connecte.";
  if (status === 403) return "Utilisateur connecte mais non autorise.";
  return `Erreur API (${status}).`;
}

export function normalizeApiBaseUrl(value: string | undefined) {
  if (!value) return "";
  return value.trim().replace(/\/+$/, "");
}
